import React, {
  useState,
  useCallback,
  useEffect,
  useRef,
  type JSX,
} from "react";
import {
  AiOutlineClose,
  AiOutlineFile,
  AiOutlineFileSync,
} from "react-icons/ai";
import {
  FiUpload,
  FiCheckCircle,
  FiAlertCircle,
  FiClock,
} from "react-icons/fi";
import type {
  Document,
  UploadProgress,
  StreamUploadResult,
  FileStatus,
} from "../../../shared/types/document";
import { useAppSelector } from "../../../redux/store";
import { deleteDocument, documentService } from "../../../shared/services/apis";
import Dialog from "../../../shared/ui/dialog";
import { useBrand } from "../../../context/brandContext";
import LoadingOverlay from "../../../shared/ui/LoadingOverlay";
import toast from "react-hot-toast";
import { RiDeleteBinLine } from "react-icons/ri";
import { DocumentStatus } from "../../../constants/enum";

const statusStyles: Record<
  string,
  { bg: string; text: string; icon: JSX.Element; label: string }
> = {
  [DocumentStatus.COMPLETED]: {
    bg: "bg-teal-50 border-teal-200",
    text: "text-teal-700",
    icon: <FiCheckCircle className="text-teal-500 w-4 h-4" />,
    label: "Analysis Complete",
  },
  [DocumentStatus.FAILED]: {
    bg: "bg-rose-50 border-rose-200",
    text: "text-rose-700",
    icon: <FiAlertCircle className="text-rose-500 w-4 h-4" />,
    label: "Analysis Failed",
  },
  [DocumentStatus.IN_PROGRESS]: {
    bg: "bg-violet-50 border-violet-200",
    text: "text-violet-700",
    icon: (
      <AiOutlineFileSync className="text-violet-600 w-4 h-4 animate-spin" />
    ),
    label: "Analyzing...",
  },
  [DocumentStatus.PENDING]: {
    bg: "bg-orange-50 border-orange-200",
    text: "text-orange-700",
    icon: <FiClock className="text-orange-500 w-4 h-4" />,
    label: "Queued for Analysis",
  },
};

interface DocumentUploadModalProps {
  isOpen: boolean;
  onClose: () => void;
  tenantId: string;
  tenantName: string;
  onUploadSuccess?: (documents: Document[]) => void;
}

interface FileUploadState {
  file: File;
  progress: UploadProgress | null;
  status: "pending" | "uploading" | "completed" | "error" | "processing";
  error?: string;
  documentId?: string;
  uploadedAt?: Date;
  ingestionStartedAt?: string;
  fileStatus?: FileStatus;
  documentType?: string;
  tempId?: string;
}

const MAX_FILE_SIZE_MB = 50;

const DocumentUploadModal: React.FC<DocumentUploadModalProps> = ({
  isOpen,
  onClose,
  tenantId,
  tenantName,
  onUploadSuccess,
}) => {
  const [files, setFiles] = useState<FileUploadState[]>([]);
  const [dragActive, setDragActive] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadComplete, setUploadComplete] = useState(false);
  const [pollingActive, setPollingActive] = useState(false);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [justUploadedIds, setJustUploadedIds] = useState<Set<string>>(
    new Set()
  );
  const pollingIntervalRef = useRef<number | null>(null);
  const pollingIntervallRef = useRef<ReturnType<typeof setInterval> | null>(
    null
  );

  const auth = useAppSelector((state) => state.auth);
  const brandId = auth.user?.brandId || "";
  const userId = auth.user?.id || "";
  const brand = useBrand();
  const tenant = brand.brand?.tenants?.find((t) => t.id === tenantId);
  const [localDocuments, setLocalDocuments] = useState<Document[]>(
    tenant?.documents?.map((doc) => ({
      ...doc,
      fileStatus: (doc as any).fileProgress?.tasks, // enrich initial documents if available
    })) || []
  );
  // const [documentType,] = useState("");
  const [error, setError] = useState("");

  // const handleSelectChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
  //   setDocumentType(e.target.value);
  // };

  // Polling backend for document status updates
  const checkDocumentStatus = useCallback(async () => {
    try {
      const updatedDocuments = await documentService.getDocumentsByTenant(
        tenantId
      );

      const enrichedDocs = updatedDocuments.map((doc) => ({
        ...doc,
        fileStatus: (doc as any).fileProgress?.tasks,
      }));

      // setLocalDocuments(enrichedDocs);
      setLocalDocuments((prevDocs) => {
        const isEqual =
          JSON.stringify(prevDocs) === JSON.stringify(enrichedDocs);
        return isEqual ? prevDocs : enrichedDocs;
      });

      // Sync Files upload states
      setFiles((prevFiles) =>
        prevFiles.map((f) => {
          if (!f.documentId) return f;
          const matchingDoc = enrichedDocs.find((d) => d.id === f.documentId);
          if (!matchingDoc) return f;

          if (matchingDoc.ingestionStatus === DocumentStatus.COMPLETED) {
            return { ...f, status: "completed" };
          }
          if (matchingDoc.ingestionStatus === DocumentStatus.FAILED) {
            return { ...f, status: "error", error: "Analysis failed" };
          }
          if (matchingDoc.ingestionStatus === DocumentStatus.IN_PROGRESS) {
            return { ...f, status: "processing" };
          }
          return f;
        })
      );

      // Stop polling if none in progress
      const stillInProgress = enrichedDocs.filter(
        (doc) =>
          doc.ingestionStatus === DocumentStatus.IN_PROGRESS ||
          doc.ingestionStatus === DocumentStatus.PENDING
      );
      if (stillInProgress.length === 0) {
        if (pollingIntervalRef.current) {
          clearInterval(pollingIntervalRef.current);
          pollingIntervalRef.current = null;
        }
        setPollingActive(false);
      }

      if (onUploadSuccess) onUploadSuccess(enrichedDocs);
    } catch (err) {
      console.error("Polling failed", err);
    }
  }, [tenantId, onUploadSuccess]);

  useEffect(() => {
    const hasInProgress = localDocuments.some(
      (doc) => doc.ingestionStatus === DocumentStatus.IN_PROGRESS
    );

    // If no docs in progress, clear interval and do nothing
    if (!hasInProgress) {
      if (pollingIntervallRef.current) {
        clearInterval(pollingIntervallRef.current);
        pollingIntervallRef.current = null;
      }
      return;
    }

    // If interval already running, do nothing (prevent multiple intervals)
    if (pollingIntervallRef.current) return;

    // Call immediately when starting polling
    checkDocumentStatus();

    // Start polling every 15 seconds
    pollingIntervallRef.current = setInterval(() => {
      checkDocumentStatus();
    }, 15000);

    // Cleanup interval on unmount or if dependencies change
    return () => {
      if (pollingIntervallRef.current) {
        clearInterval(pollingIntervallRef.current);
        pollingIntervallRef.current = null;
      }
    };
  }, []);

  const startPolling = useCallback(() => {
    if (pollingIntervalRef.current) return;
    setPollingActive(true);
    pollingIntervalRef.current = window.setInterval(checkDocumentStatus, 3000);
  }, [checkDocumentStatus]);

  const stopPolling = useCallback(() => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
    setPollingActive(false);
  }, []);

  useEffect(() => {
    if (files.length === 0) return;
    const allFinished = files.every(
      (f) => f.status === "completed" || f.status === "error"
    );
    if (allFinished) {
      const timer = setTimeout(() => {
        resetUpload();
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [files]);

  useEffect(() => {
    if (!localDocuments.length) return;
    const docsInProgress = localDocuments.filter(
      (doc) =>
        doc.ingestionStatus === DocumentStatus.IN_PROGRESS ||
        doc.ingestionStatus === DocumentStatus.PENDING
    );
    if (docsInProgress.length > 0 && !pollingActive) {
      startPolling();
    } else if (docsInProgress.length === 0 && pollingActive) {
      stopPolling();
    }
  }, [localDocuments, startPolling, stopPolling]); //removved pollingActive from dependencyyy

  useEffect(() => {
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (tenant?.documents) {
      setLocalDocuments(
        tenant.documents.map((doc) => ({
          ...doc,
          fileStatus: (doc as any).fileProgress?.tasks,
        }))
      );
    }
  }, [tenant?.documents]);

  useEffect(() => {
    if (!isOpen) {
      stopPolling();
      setTimeout(() => {
        setJustUploadedIds(new Set());
      }, 1000);
    }
  }, [isOpen, stopPolling]);

  // Drag and Drop Handlers
  const handleDrag = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(e.type === "dragenter" || e.type === "dragover");
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files?.[0]) {
      handleFiles(Array.from(e.dataTransfer.files));
    }
  }, []);

  /// multiple file upload
  const allowedTypes = [
    "application/pdf",
    "text/csv",
    ".csv",
    "application/vnd.ms-excel", // .xls
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet", // .xlsx
    "application/vnd.ms-excel.sheet.macroEnabled.12", // .xlsm
    "application/vnd.ms-powerpoint", // .ppt
    "application/vnd.openxmlformats-officedocument.presentationml.presentation", // .pptx
  ];
  const handleFiles = (newFiles: File[]) => {
    const validFiles: FileUploadState[] = [];
    const errors: string[] = [];
    newFiles.forEach((file, i) => {
      if (!allowedTypes.includes(file.type)) {
        errors.push(`"${file.name}" is not an allowed file type.`);
        return;
      }
      if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
        errors.push(
          `"${file.name}" exceeds the ${MAX_FILE_SIZE_MB} MB size limit.`
        );
        return;
      }
      // Prevent duplicate files by name and size
      const alreadyAdded = files.some(
        (f) => f.file.name === file.name && f.file.size === file.size
      );
      if (alreadyAdded) {
        errors.push(` "${file.name}" is already added.`);
        return;
      }
      validFiles.push({
        file,
        progress: null,
        status: "pending",
        tempId: `${file.name}-${file.size}-${Date.now()}-${i}`,
      });
    });
    if (validFiles.length > 0) {
      setFiles((prev) => [...prev, ...validFiles]);
    }
    if (errors.length > 0) {
      setValidationErrors(errors);
      // Clear validation errors after 5 seconds
      setTimeout(() => setValidationErrors([]), 5000);
    }
  };

  /// Single File Upload
  // const handleFiles = (newFiles: File[]) => {
  //   const errors: string[] = [];

  //   const file = newFiles[0];
  //   if (!file) return;
  //   const allowedTypes = [
  //     "application/pdf",
  //     "text/csv",
  //     "application/vnd.ms-excel",
  //     "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  //     "application/vnd.ms-excel.sheet.macroEnabled.12",
  //     "application/vnd.ms-powerpoint",
  //     "application/vnd.openxmlformats-officedocument.presentationml.presentation",
  //   ];
  //   if (!allowedTypes.includes(file.type)) {
  //     errors.push(` "${file.name}" is not a PDF or allowed file type.`);
  //   } else if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
  //     errors.push(
  //       `"${file.name}" exceeds the ${MAX_FILE_SIZE_MB} MB size limit.`
  //     );
  //   } else {
  //     setFiles([
  //       {
  //         file,
  //         progress: null,
  //         status: "pending",
  //       },
  //     ]);
  //   }

  //   if (errors.length > 0) {
  //     setValidationErrors(errors);
  //     setTimeout(() => setValidationErrors([]), 5000);
  //   }
  // };

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) handleFiles(Array.from(e.target.files)); /// multiple fileUpload
    e.target.value = ""; // reset input to allow re-upload of same file if needed

    // const file = e.target.files?.[0];
    // if (file) handleFiles([file]);
    // e.target.value = "";
  };

  const uploadFile = async (fileIndex: number) => {
    const fileState = files[fileIndex];
    if (!fileState) return;
    const updateFileState = (updates: Partial<FileUploadState>) => {
      setFiles((prev) =>
        prev.map((f, i) => (i === fileIndex ? { ...f, ...updates } : f))
      );
    };
    try {
      updateFileState({ status: "uploading" });
      const result: StreamUploadResult = await documentService.uploadSingleFile(
        fileState.file,
        tenantId,
        brandId,
        userId,
        {
          enableCompression: true,
          priority: 1,
          metadata: {
            uploadedBy: "tenant-upload-modal",
            tenantName,
            documentType: fileState.documentType || "",
          },
        }
      );

      updateFileState({
        status: "processing",
        documentId: result.documentId,
        uploadedAt: new Date(),
        progress: {
          ...fileState.progress,
          percentage: 100,
          status: "COMPLETED",
        } as UploadProgress,
      });

      // Add to local documents with fileStatus empty initially; will update on polling
      setLocalDocuments((prevDocs) => [
        ...prevDocs,
        {
          id: result.documentId,
          name: fileState.file.name,
          fileSize: fileState.file.size,
          ingestionStatus: DocumentStatus.IN_PROGRESS,
          startedAt: new Date().toISOString(),
          isActive: true,
          documentType: fileState.documentType || "",
          fileStatus: fileState.fileStatus, // will be fetched on polling
        } as Document,
      ]);

      setJustUploadedIds((prev) => new Set(prev).add(result.documentId));

      startPolling();
    } catch (error) {
      updateFileState({
        status: "error",
        error: error instanceof Error ? error.message : "Upload failed",
      });
    }
  };

  const uploadAllFiles = async () => {
    // if (!documentType) {
    //   setError("Please select a file type before uploading.");
    //   setTimeout(() => {
    //     setError("");
    //   }, 3000);
    //   return;
    // }
    const missingTypeFiles = files.filter(
      (f) => f.status === "pending" && !f.documentType
    );

    if (missingTypeFiles.length > 0) {
      setError(
        "Please select a document type for all pending files before uploading."
      );
      setTimeout(() => setError(""), 4000);
      return;
    }

    setUploading(true);
    try {
      const uploadPromises = files
        .map((_, index) => index)
        .filter((index) => files[index].status === "pending")
        .map((index) => uploadFile(index));
      await Promise.all(uploadPromises);

      setUploadComplete(true);
    } catch (err) {
      console.error(err);
    } finally {
      setTimeout(() => {
        setUploading(false);
        setError("");
      }, 7000);
    }
  };

  const removeFile = (index: number) =>
    setFiles((prev) => prev.filter((_, i) => i !== index));

  const resetUpload = () => {
    setFiles([]);
    setUploadComplete(false);
    setUploading(false);
    setJustUploadedIds(new Set());
  };

  const handleDelete = async (documentId: string) => {
    try {
      const response = await deleteDocument(documentId);
      if (response) {
        toast.success(response.message || "Document deleted successfully");
        setLocalDocuments((prevDocs) =>
          prevDocs.filter((doc) => doc.id !== documentId)
        );
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || "Failed to delete document");
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

 function calculateDocumentCompletion(doc: any, status: DocumentStatus): string {
  const parsingProgress =
    doc?.fileStatus?.parsing?.total_pages > 0
      ? (doc?.fileStatus?.parsing?.parsed_pages ?? 0) /
        doc?.fileStatus?.parsing?.total_pages
      : 0;

  const checkpoint = doc?.fileStatus?.summary?.checkpoint;

  let summaryProgress;

  switch (checkpoint) {
    case "summary Started":
      summaryProgress = 0;
      break;
    case "hierarchical summary":
      summaryProgress = 0.5;
      break;
    case "Chunked summary":
      summaryProgress = 1;
      break;
    default:
      summaryProgress =
        doc?.fileStatus?.summary?.total_pages > 0
          ? (doc?.fileStatus?.summary?.summarized_pages ?? 0) /
            doc?.fileStatus?.summary?.total_pages
          : 0;
      break;
  }

  const avgProgress = ((parsingProgress + summaryProgress) / 2) * 100;

  if (status === DocumentStatus.IN_PROGRESS) {
    return avgProgress.toFixed(2);
  } else if (status === DocumentStatus.COMPLETED) {
    return "100";
  } else {
    return avgProgress.toFixed(2);
  }
}


  const getFileStatusDisplay = (fileState: FileUploadState) => {
    switch (fileState.status) {
      case "uploading":
        return {
          icon: (
            <AiOutlineFileSync className="text-violet-600 w-4 h-4 animate-spin" />
          ),
          text: "Uploading...",
          bg: "bg-violet-50 border-violet-200",
          textColor: "text-violet-700",
        };
      case "processing":
        return {
          icon: (
            <AiOutlineFileSync className="text-orange-600 w-4 h-4 animate-spin" />
          ),
          text: "Analyzing...",
          bg: "bg-orange-50 border-orange-200",
          textColor: "text-orange-700",
        };
      case "completed":
        return {
          icon: <FiCheckCircle className="text-teal-500 w-4 h-4" />,
          text: "Uploaded",
          bg: "bg-teal-50 border-teal-200",
          textColor: "text-teal-700",
        };
      case "error":
        return {
          icon: <FiAlertCircle className="text-rose-500 w-4 h-4" />,
          text: "Failed",
          bg: "bg-rose-50 border-rose-200",
          textColor: "text-rose-700",
        };
      default:
        return {
          icon: <FiClock className="text-gray-400 w-4 h-4" />,
          text: "Pending",
          bg: "bg-gray-50 border-gray-200",
          textColor: "text-gray-600",
        };
    }
  };

  if (!isOpen) return null;

  const allDocuments = [
    ...(localDocuments || tenant?.documents || []),
    ...files
      .filter((f) => f.status === "processing" || f.status === "completed")
      .map((f) => ({
        id: f.documentId || `temp-${f.file.name}`,
        name: f.file.name,
        fileSize: f.file.size,
        ingestionStatus:
          f.status === "processing"
            ? DocumentStatus.IN_PROGRESS
            : DocumentStatus.COMPLETED,
        startedAt: f.uploadedAt?.toISOString() || new Date().toISOString(),
        ingestionStartedAt: f.ingestionStartedAt,
        isJustUploaded: true,
        isActive: true,
        documentType: f.documentType || "",
        fileStatus: f.progress?.tasks,
      })),
  ]
    .filter((doc) => doc.isActive)
    .filter((doc) => doc.ingestionStartedAt != null)
    .filter(
      (doc, index, arr) => arr.findIndex((d) => d.id === doc.id) === index
    )
    .sort(
      (a, b) =>
        new Date(b.startedAt).getTime() - new Date(a.startedAt).getTime()
    );

  // console.log("All documents with fileStatus:", allDocuments);

  return (
    <Dialog
      isOpen={isOpen}
      onClose={onClose}
      title={`Upload Documents for ${tenantName}`}
      size="lg"
    >
      <div className="space-y-8">
        {/* Uploaded documents list */}
        <section
          aria-label="Document library"
          className=" rounded-2xl border border-gray-200/60 shadow-sm overflow-hidden"
        >
          <div className="bg-white/60 backdrop-blur-sm border-b border-gray-200/60 px-6 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div>
                  <h3 className="text-lg font-light text-gray-700">
                    Document Library
                  </h3>
                  <p className="text-sm text-slate-600">
                    {allDocuments.length} document
                    {allDocuments.length !== 1 ? "s" : ""}
                  </p>
                </div>
              </div>
              {pollingActive && (
                <div className="flex items-center gap-2 text-sm text-gray-600 bg-[#FDFDFD] px-3 py-1.5 rounded-full border border-purple-200">
                  <AiOutlineFileSync className="w-4 h-4 animate-spin" />
                  <span className="font-medium">Live Updates</span>
                </div>
              )}
            </div>
          </div>
          <div className="p-6 max-h-40 overflow-y-auto">
            {allDocuments.length > 0 && (
              <div className="space-y-3">
                {allDocuments.map((doc) => {
                  const ingestionStyle =
                    statusStyles[doc.ingestionStatus] ||
                    statusStyles[DocumentStatus.PENDING];
                  const isNew =
                    justUploadedIds.has(doc.id) ||
                    ("isJustUploaded" in doc && !!(doc as any).isJustUploaded);

                  return (
                    <div
                      key={doc.id}
                      className={`group relative flex items-center gap-4 bg-white rounded-xl p-4 border shadow-sm transition-all duration-300 hover:shadow-md hover:border-gray-300 ${
                        isNew
                          ? "ring-2 ring-gray-200 border-gray-300 animate-pulse"
                          : "border-gray-200"
                      }`}
                      role="listitem"
                    >
                      {isNew && (
                        <div className="absolute -top-2 -right-2 bg-gradient-to-r from-purple-500 to-violet-600 text-white text-xs font-bold px-2 py-1 rounded-full shadow-lg animate-bounce">
                          NEW
                        </div>
                      )}

                      <div className="p-2.5 bg-gray-50 rounded-lg group-hover:bg-gray-100 transition-all duration-300">
                        <AiOutlineFile className="text-gray-600 text-lg transition-colors duration-300" />
                      </div>

                      <div className="flex-grow min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <p
                            className="font-light text-gray-900 truncate group-hover:text-black transition-colors duration-300"
                            title={doc.name}
                          >
                            {doc.name}{" "}
                            <p className="text-xs text-gray-500">
                              {doc.documentType}
                            </p>
                          </p>

                          {isNew && (
                            <span className="inline-flex items-center px-1.5 py-0.5 rounded-md text-xs font-medium bg-teal-100 text-teal-800 animate-pulse">
                              Just uploaded
                            </span>
                          )}
                        </div>

                        <div className="flex items-center gap-3 text-sm text-gray-500">
                          {(doc.fileStatus?.summary ||
                            doc.fileStatus?.parsing) && (
                            <div className=" text-gray-600">
                              Document Completion:{" "}
                              {calculateDocumentCompletion(doc, doc.ingestionStatus)}%
                            </div>
                          )}
                        </div>
                        <div className="flex items-center gap-3 text-sm text-gray-500">
                          {/* <span>{formatFileSize(doc.fileSize || 0)}</span> */}
                          {/* <span>•</span> */}
                          <span>
                            {isNew
                              ? "Just now"
                              : `Uploaded ${new Date(
                                  doc.startedAt
                                ).toLocaleString()}`}
                          </span>
                        </div>
                      </div>

                      {ingestionStyle.label !== "Analyzing..." && (
                        <button
                          onClick={() => handleDelete(doc.id)}
                          type="button"
                          className="p-2 text-gray-400 hover:text-black hover:bg-gray-50 rounded-full transition-colors duration-200 cursor-pointer"
                          title="Delete document"
                        >
                          <RiDeleteBinLine className="w-4 h-4" />
                        </button>
                      )}

                      <div
                        className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-semibold transition-all duration-300 border ${ingestionStyle.bg} ${ingestionStyle.text}`}
                      >
                        {ingestionStyle.icon}
                        <span>{ingestionStyle.label}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </section>
        {error && <p className="text-red-500 text-sm">{error}</p>}

        {/* Upload new files */}
        <section aria-label="Upload new files" className="space-y-6">
          <div className="flex items-center gap-3">
            <div>
              <h3 className="text-lg font-light text-gray-700">
                Upload Financial and Other Data
              </h3>
              <p className="text-gray-600 font-light">
                Only one file can be uploaded at a time.{" "}
                <span className="text-xs text-gray-600">
                  (If multiple are selected, only the first will be used)
                </span>
              </p>
            </div>
          </div>

          {validationErrors.length > 0 && (
            <div
              className="bg-rose-50 border border-rose-200 rounded-lg p-4 space-y-2"
              role="alert"
            >
              <div className="flex items-center gap-2 text-rose-800 font-semibold">
                <FiAlertCircle className="w-5 h-5" />
                <span>Upload Issues</span>
              </div>
              <div className="space-y-1">
                {validationErrors.map((err, idx) => (
                  <p key={idx} className="text-sm text-rose-700 pl-7">
                    {err}
                  </p>
                ))}
              </div>
            </div>
          )}

          {files.length > 0 && (
            <div className="border border-gray-200 rounded-xl overflow-hidden">
              <div className="px-4 py-3 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <h4 className="font-light text-gray-800">Upload Queue</h4>
                  <button
                    onClick={resetUpload}
                    className="text-sm text-gray-600 hover:text-rose-600 transition-colors duration-200"
                    type="button"
                  >
                    Clear all
                  </button>
                </div>
              </div>
              {/* <div className="max-h-64 overflow-y-auto divide-y divide-gray-200">
                {files.map((fileState, i) => {
                  const statusDisplay = getFileStatusDisplay(fileState);

                  return (
                    <div
                      key={i}
                      className="flex items-center justify-between p-4 hover:bg-white transition-colors duration-200"
                    >
                      <div className="flex items-center gap-3 flex-grow min-w-0">
                        <div
                          className={`p-2 rounded-lg border ${statusDisplay.bg}`}
                        >
                          <AiOutlineFile className="text-gray-600 w-4 h-4" />
                        </div>
                        <div className="min-w-0 flex-grow">
                          <p
                            className="font-light text-gray-800 truncate"
                            title={fileState.file.name}
                          >
                            {fileState.file.name}
                          </p>
                          <p className="text-sm text-gray-500">
                            {formatFileSize(fileState.file.size)}
                          </p>
                          {fileState.error && (
                            <p
                              className="text-xs text-rose-600 mt-1 truncate"
                              title={fileState.error}
                            >
                              {fileState.error}
                            </p>
                          )}
                        </div>
                      </div>

                      <div className="flex items-center gap-3">
                        <div
                          className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-medium border ${statusDisplay.bg} ${statusDisplay.textColor}`}
                        >
                          {statusDisplay.icon}
                          <span>{statusDisplay.text}</span>
                        </div>

                        {fileState.status === "pending" && (
                          <button
                            onClick={() => removeFile(i)}
                            type="button"
                            className="p-2 text-gray-400 hover:text-black hover:bg-gray-50 rounded-full transition-colors duration-200 cursor-pointer"
                            title="Remove file"
                          >
                            <AiOutlineClose className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div> */}
              <div className="max-h-64 overflow-y-auto divide-y divide-gray-200">
                {files.map((fileState, idx) => {
                  const statusDisplay = getFileStatusDisplay(fileState);

                  return (
                    <div
                      key={fileState.file.name + "-" + idx}
                      className="flex items-center justify-between p-4 hover:bg-white transition-colors duration-200"
                    >
                      <div className="flex items-center gap-3 flex-grow min-w-0">
                        <div
                          className={`p-2 rounded-lg border ${statusDisplay.bg}`}
                        >
                          <AiOutlineFile className="text-gray-600 w-4 h-4" />
                        </div>
                        <div className="min-w-0 flex-grow">
                          <p
                            className="font-light text-gray-800 truncate"
                            title={fileState.file.name}
                          >
                            {fileState.file.name}
                          </p>
                          <p className="text-sm text-gray-500">
                            {formatFileSize(fileState.file.size)}
                          </p>
                          {fileState.error && (
                            <p
                              className="text-xs text-rose-600 mt-1 truncate"
                              title={fileState.error}
                            >
                              {fileState.error}
                            </p>
                          )}

                          {/* Document type select per file */}
                          <select
                            value={fileState.documentType || ""} // Store documentType directly in fileState
                            onChange={(e) => {
                              const newType = e.target.value;

                              // Update file's documentType at its index
                              setFiles((prev) =>
                                prev.map((f, i) =>
                                  i === idx
                                    ? { ...f, documentType: newType }
                                    : f
                                )
                              );
                            }}
                            className="mt-2 w-1/2 px-3 py-1 border border-gray-300 rounded-md text-gray-700 font-light"
                          >
                            <option value="">Select File Type</option>
                            <option value="CSV">CSV</option>
                            <option value="FIN_REPORTS">
                              Financial Report
                            </option>
                            <option value="SLIDES">Slides</option>
                            <option value="TRANSCRIPTS">Transcripts</option>
                          </select>
                        </div>
                      </div>

                      <div className="flex items-center gap-3">
                        <div
                          className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-medium border ${statusDisplay.bg} ${statusDisplay.textColor}`}
                        >
                          {statusDisplay.icon}
                          <span>{statusDisplay.text}</span>
                        </div>

                        {fileState.status === "pending" && (
                          <button
                            onClick={() => removeFile(idx)}
                            type="button"
                            className="p-2 text-gray-400 hover:text-black hover:bg-gray-50 rounded-full transition-colors duration-200 cursor-pointer"
                            title="Remove file"
                          >
                            <AiOutlineClose className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
          {/* <select
            value={documentType}
            onChange={handleSelectChange}
            className="w-full px-6 py-3 border text-gray-700 font-light border-gray-200 rounded-xl  "
          >
            <option value="">Select File Type</option>
            <option value="CSV">CSV</option>
            <option value="FIN_REPORTS">Financial Report</option>
            <option value="SLIDES">Slides</option>
            <option value="TRANSCRIPTS">Transcripts</option>
          </select> */}
          {/* Drag & Drop zone */}
          <label
            htmlFor="file-upload"
            className={`group relative block cursor-pointer rounded-2xl border border-dashed p-12 text-center transition-all duration-300 hover:scale-[1.02] ${
              dragActive
                ? "border-gray-400 bg-white shadow-lg"
                : "border-gray-300 hover:border-gray-400 hover:bg-[#FDFDFD]"
            }`}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
          >
            <div className="relative">
              <div
                className={`mx-auto w-16 h-16 rounded-full bg-gray-800 flex items-center justify-center mb-4 shadow-lg transition-transform duration-300 group-hover:scale-110 ${
                  dragActive ? "animate-pulse scale-110" : ""
                }`}
              >
                <FiUpload className="text-white text-2xl" />
              </div>
              <p className="text-lg  text-gray-800 mb-2">
                {dragActive ? "Drop files here!" : "Drag & drop PDF files"}
              </p>
              <p className="text-sm text-gray-600">
                or <span className="text-blue-600 ">browse</span>
              </p>
              <p className="text-xs text-gray-500 mt-2">
                Maximum file size: {MAX_FILE_SIZE_MB}MB • PDF files only
              </p>
            </div>
            <input
              id="file-upload"
              type="file"
              accept="
               application/pdf,
                .csv,
                .xls,
                .xlsx,
                .xlsm,
               application/vnd.ms-excel,
               application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,
               application/vnd.ms-powerpoint,
               application/vnd.openxmlformats-officedocument.presentationml.presentation
               "
              multiple
              onChange={handleFileInput}
              className="hidden"
            />
          </label>
        </section>

        {/* Upload actions */}
        <div className="sticky -bottom-4 bg-white border-t border-gray-200 py-4  px-2">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-2 text-sm text-gray-600">
              {uploadComplete && (
                <>
                  <FiCheckCircle className="text-teal-500 w-4 h-4" />
                  <span>Upload completed! Files are being analyzed.</span>
                </>
              )}
            </div>

            <div className="flex gap-3">
              <button
                onClick={resetUpload}
                disabled={uploading || files.length === 0}
                type="button"
                className="px-6 py-2.5 text-sm font-semibold text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Reset
              </button>
              <button
                onClick={uploadAllFiles}
                disabled={
                  uploading ||
                  files.length === 0 ||
                  files.every((f) => f.status !== "pending")
                }
                type="button"
                className="inline-flex items-center gap-2 px-6 py-2.5 text-sm font-semibold text-white bg-black rounded-lg shadow-md hover:shadow-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {uploading ? (
                  <>
                    <AiOutlineFileSync className="w-4 h-4 animate-spin" />
                    <span>Uploading...</span>
                  </>
                ) : (
                  <>
                    <FiUpload className="w-4 h-4" />
                    <span>
                      Upload{" "}
                      {files.filter((f) => f.status === "pending").length} File
                      {files.filter((f) => f.status === "pending").length !== 1
                        ? "s"
                        : ""}
                    </span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
      {uploading && (
        <LoadingOverlay
          onMinimize={() => {
            setUploading(false), onClose();
          }}
        />
      )}
    </Dialog>
  );
};

export default DocumentUploadModal;
